import type { StatisticsPeriod } from '@/types/statistics'

/** The academic year a semester code belongs to: B261 and B262 are 2026 (2026/27), A991 is 1999. */
export function academicYearOf(semester: string): number {
    return (semester.startsWith('A') ? 1900 : 2000) + Number(semester.slice(1, 3))
}

/** The season and the full academic years of a semester code: B201 is winter 2020/2021. */
export function semesterParts(semester: string): { season: 'winter' | 'summer'; years: string } {
    const year = academicYearOf(semester)
    return { season: semester.endsWith('1') ? 'winter' : 'summer', years: `${year}/${year + 1}` }
}

/** The winter semester that opens an academic year: 2020 is B201, 1999 is A991. */
export function winterSemesterOf(year: number): string {
    return `${year < 2000 ? 'A' : 'B'}${String(year % 100).padStart(2, '0')}1`
}

export function academicYearLabel(year: number): string {
    return `${year}/${String((year + 1) % 100).padStart(2, '0')}`
}

export function periodQuery(period: StatisticsPeriod, username?: string): URLSearchParams {
    const query = new URLSearchParams()
    if ('semester' in period) {
        query.set('semester', period.semester)
    } else {
        query.set('year', String(period.year))
    }
    if (username) {
        query.set('username', username)
    }
    return query
}

/** The semester code, or the year as a string. */
export function periodLabel(period: StatisticsPeriod): string {
    return 'semester' in period ? period.semester : String(period.year)
}

/** The period a route query names; without a valid one, the fallback semester, or null when there is none. */
export function periodFromQuery(query: Record<string, unknown>, fallbackSemester: string | null): StatisticsPeriod | null {
    const { year, semester } = query
    if (typeof year === 'string' && /^\d{4}$/.test(year)) {
        return { year: Number(year) }
    }
    if (typeof semester === 'string' && semester !== '') {
        return { semester }
    }
    return fallbackSemester === null ? null : { semester: fallbackSemester }
}
